import React, { useState, useEffect, useMemo } from "react";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import {
  Grid,
  Paper,
  Typography,
  Box,
  List,
  ListItemText,
  CircularProgress,
  IconButton,
  ListItemButton,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardActions,
  Menu,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SortIcon from "@mui/icons-material/Sort";
import EngineeringIcon from "@mui/icons-material/Engineering";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CreateEngineer from "./CreateEngineer";

function EngineerManagement({ onEngineerSelect }) {
  const [engineers, setEngineers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteError, setDeleteError] = useState(null);
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("lastName");
  const [viewMode, setViewMode] = useState("list"); // list or grid
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, engineer: null });
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const engineersQuery = query(
      collection(db, "users"),
      where("role", "==", "engineer"),
      orderBy("lastName", "asc")
    );

    const unsubscribe = onSnapshot(
      engineersQuery,
      (querySnapshot) => {
        const engineersList = [];
        querySnapshot.forEach((doc) => {
          engineersList.push({ id: doc.id, ...doc.data() });
        });
        setEngineers(engineersList);
        setIsLoading(false);
      },
      (err) => {
        console.error("Error fetching engineers:", err);
        setIsLoading(false);
        setSnackbar({
          open: true,
          message: "Error loading engineers",
          severity: "error"
        });
      }
    );

    return () => unsubscribe();
  }, []);

  // Filter and sort engineers
  const filteredAndSortedEngineers = useMemo(() => {
    let filtered = [...engineers];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (eng) =>
          (eng.firstName?.toLowerCase().includes(search)) ||
          (eng.lastName?.toLowerCase().includes(search)) ||
          (eng.email?.toLowerCase().includes(search))
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "firstName":
          return (a.firstName || "").localeCompare(b.firstName || "");
        case "lastName":
          return (a.lastName || "").localeCompare(b.lastName || "");
        case "email":
          return (a.email || "").localeCompare(b.email || "");
        case "newest":
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
        case "oldest":
          return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [engineers, searchTerm, sortBy]);

  // Statistics
  const stats = useMemo(() => ({
    total: engineers.length,
    filtered: filteredAndSortedEngineers.length,
    active: engineers.filter(e => e.isActive !== false).length,
    withPhone: engineers.filter(e => e.phone).length,
  }), [engineers, filteredAndSortedEngineers]);

  const handleDeleteClick = (engineer) => {
    setDeleteDialog({ open: true, engineer });
  };

  const handleDeleteConfirm = async () => {
    const { engineer } = deleteDialog;
    setDeleteDialog({ open: false, engineer: null });
    setDeleteError(null);

    try {
      const deleteEngineerFunc = httpsCallable(functions, "deleteEngineer");
      await deleteEngineerFunc({ uid: engineer.id });
      
      setSnackbar({
        open: true,
        message: `Engineer "${engineer.firstName} ${engineer.lastName}" deleted successfully`,
        severity: "success"
      });
    } catch (err) {
      console.error("Error deleting engineer: ", err);
      setDeleteError(`Error: ${err.message}`);
      setSnackbar({
        open: true,
        message: `Failed to delete engineer: ${err.message}`,
        severity: "error"
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, engineer: null });
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0) || "";
    const last = lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || "?";
  };

  const handleEngineerCreated = (newEngineer) => {
    setSnackbar({
      open: true,
      message: "Engineer created successfully!",
      severity: "success"
    });
  };

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Avatar
            sx={{
              bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              width: 56,
              height: 56,
            }}
          >
            <EngineeringIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Engineer Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create engineer accounts and manage who can receive job assignments.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Statistics Dashboard */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {stats.total}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Total Engineers
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {stats.filtered}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {searchTerm ? "Matching Search" : "Active Engineers"}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {stats.active}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Active Status
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
              color: "white",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {stats.withPhone}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              With Phone Number
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Left: Create Engineer */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 2.5,
              borderRadius: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ bgcolor: "#667eea" }}>
                <PersonIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Add New Engineer
              </Typography>
            </Box>
            <Divider />
            <Typography variant="body2" color="text.secondary">
              Create a new engineer login linked to an email address.
            </Typography>
            <CreateEngineer onEngineerCreated={handleEngineerCreated} />
          </Paper>
        </Grid>

        {/* Right: Engineer list */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{
              p: 2.5,
              borderRadius: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              All Engineers
            </Typography>

            {/* Search and Controls */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search engineers by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                  startAdornment={<SortIcon fontSize="small" sx={{ mr: 1, ml: 1 }} />}
                >
                  <MenuItem value="lastName">Last Name</MenuItem>
                  <MenuItem value="firstName">First Name</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </Select>
              </FormControl>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {/* Result counter */}
            {searchTerm && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Showing {stats.filtered} of {stats.total} engineers
              </Typography>
            )}

            {deleteError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
                {deleteError}
              </Alert>
            )}

            {isLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}

            {!isLoading && engineers.length === 0 && (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <EngineeringIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: "#e5e7eb" }}>
                  No Engineers Yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use the form on the left to add your first engineer.
                </Typography>
              </Box>
            )}

            {!isLoading && filteredAndSortedEngineers.length === 0 && engineers.length > 0 && (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <SearchIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No Matching Engineers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No engineers match your search criteria.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setSearchTerm("")}
                  size="small"
                >
                  Clear Search
                </Button>
              </Box>
            )}

            {/* List View */}
            {!isLoading && filteredAndSortedEngineers.length > 0 && viewMode === "list" && (
              <List dense sx={{ mt: 1 }}>
                {filteredAndSortedEngineers.map((engineer) => {
                  const displayName =
                    `${engineer.firstName || ""} ${
                      engineer.lastName || ""
                    }`.trim() || engineer.email;

                  return (
                    <EngineerListItem
                      key={engineer.id}
                      engineer={engineer}
                      displayName={displayName}
                      onSelect={onEngineerSelect}
                      onDelete={handleDeleteClick}
                      getInitials={getInitials}
                    />
                  );
                })}
              </List>
            )}

            {/* Grid View */}
            {!isLoading && filteredAndSortedEngineers.length > 0 && viewMode === "grid" && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {filteredAndSortedEngineers.map((engineer) => {
                  const displayName =
                    `${engineer.firstName || ""} ${
                      engineer.lastName || ""
                    }`.trim() || engineer.email;

                  return (
                    <Grid item xs={12} sm={6} key={engineer.id}>
                      <EngineerCard
                        engineer={engineer}
                        displayName={displayName}
                        onSelect={onEngineerSelect}
                        onDelete={handleDeleteClick}
                        getInitials={getInitials}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteIcon color="error" />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete engineer{" "}
            <strong>
              {deleteDialog.engineer?.firstName} {deleteDialog.engineer?.lastName}
            </strong>{" "}
            ({deleteDialog.engineer?.email})?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The engineer will lose access to the system.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Engineer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "", severity: "success" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: "", severity: "success" })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// List Item Component
function EngineerListItem({ engineer, displayName, onSelect, onDelete, getInitials }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <ListItemButton
      divider
      onClick={() => onSelect && onSelect(engineer.id)}
      sx={{
        py: 1.5,
        "&:hover": {
          backgroundColor: "#f3f4ff",
          transform: "translateX(4px)",
        },
        transition: "all 0.2s",
      }}
    >
      <Avatar
        sx={{
          bgcolor: "#667eea",
          mr: 2,
          width: 40,
          height: 40,
        }}
      >
        {getInitials(engineer.firstName, engineer.lastName)}
      </Avatar>
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 500 }}>{displayName}</Typography>
            {engineer.isActive !== false && (
              <Chip
                label="Active"
                size="small"
                color="success"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <EmailIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary">
              {engineer.email}
            </Typography>
          </Box>
        }
      />
      <IconButton
        edge="end"
        onClick={handleMenuOpen}
        sx={{ mr: 1 }}
      >
        <MoreVertIcon />
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            onSelect && onSelect(engineer.id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <VerifiedUserIcon fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDelete(engineer);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Delete Engineer</Typography>
        </MenuItem>
      </Menu>
    </ListItemButton>
  );
}

// Card Component for Grid View
function EngineerCard({ engineer, displayName, onSelect, onDelete, getInitials }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Card
      sx={{
        cursor: "pointer",
        transition: "all 0.3s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 4,
        },
      }}
      onClick={() => onSelect && onSelect(engineer.id)}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: "#667eea",
              width: 56,
              height: 56,
            }}
          >
            {getInitials(engineer.firstName, engineer.lastName)}
          </Avatar>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {displayName}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary">
            {engineer.email}
          </Typography>
        </Box>

        {engineer.isActive !== false && (
          <Chip
            label="Active"
            size="small"
            color="success"
            sx={{ mt: 1 }}
          />
        )}
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            onSelect && onSelect(engineer.id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <VerifiedUserIcon fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDelete(engineer);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Delete Engineer</Typography>
        </MenuItem>
      </Menu>
    </Card>
  );
}

export default EngineerManagement;